import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  FolderOpen,
  Settings,
  TrendingUp,
  Layers,
  Users,
  Calendar,
  DollarSign,
  Clock,
  AlertTriangle
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import projectService from '../../services/projectService'
import RevisionHistoryModal from '../../components/RevisionHistoryModal/RevisionHistoryModal'
import '../../components/RevisionHistoryModal/RevisionHistoryModal.css'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const ProjectList = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalProjects: 0,
    activeProjects: 0,
    completedProjects: 0,
    onHoldProjects: 0,
    cancelledProjects: 0,
    overdueProjects: 0,
    budgetUtilization: 0,
    averageProjectDuration: 0
  })
  const [showRevisionHistory, setShowRevisionHistory] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const [selectedProjectData, setSelectedProjectData] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [projectsResponse, statsResponse] = await Promise.all([
        projectService.getAllProjects(),
        projectService.getProjectStats()
      ])

      if (projectsResponse.success !== false && projectsResponse.projects) {
        setData(projectsResponse.projects || [])
        
        // Show toast if no data
        if ((projectsResponse.projects || []).length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No projects found. Create your first project to get started!')
        }
      } else {
        toast.error(projectsResponse.message || 'Failed to load projects')
      }

      if (statsResponse.success) {
        setStats(statsResponse.data || {})
      }
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Failed to load projects data')
    } finally {
      setLoading(false)
    }
  }

  const handleAddNew = () => {
    navigate('/projects/new')
  }

  const handleEdit = (project) => {
    navigate(`/projects/edit/${project._id}`)
  }

  const handleView = (project) => {
    navigate(`/projects/view/${project._id}`)
  }

  const handleDelete = (project) => {
    setDeleteItem(project)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      const response = await projectService.deleteProject(deleteItem._id)
      if (response.success) {
        toast.success('Project deleted successfully')
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete project')
      }
    } catch (error) {
      console.error('Error deleting project:', error)
      toast.error('Failed to delete project')
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const handleRevisionHistory = (project) => {
    setSelectedProjectData(project)
    setShowRevisionHistory(true)
  }

  const handleRowSelection = (selectedRows) => {
    setSelectedRows(selectedRows)
  }

  // Define columns for the data table
  const columns = [
    {
      key: 'projectNumber',
      label: 'Project #',
      sortable: true,
      render: (value, row) => (
        <div className="font-medium text-gray-900">
          {value}
        </div>
      )
    },
    {
      key: 'projectName',
      label: 'Project Name',
      sortable: true,
      render: (value, row) => (
        <div className="font-medium text-gray-900">{value}</div>
      )
    },
    {
      key: 'customer',
      label: 'Customer',
      sortable: true,
      render: (value, row) => {
        // Handle both object and string cases
        const customer = row.customer
        if (!customer) return <span className="text-gray-500">N/A</span>
        
        const customerName = typeof customer === 'string' 
          ? customer 
          : (customer.customerName || customer.name || 'N/A')
        
        return <div className="font-medium text-gray-900">{customerName}</div>
      }
    },
    {
      key: 'projectType',
      label: 'Type',
      sortable: true,
      render: (value, row) => {
        // Handle both object and string cases
        const projectType = row.projectType
        if (!projectType) return <span className="text-gray-500">N/A</span>
        
        const typeName = typeof projectType === 'string' 
          ? projectType 
          : (projectType.projectTypeName || projectType.name || 'N/A')
        
        return <div className="font-medium text-gray-900">{typeName}</div>
      }
    },
    {
      key: 'projectStatus',
      label: 'Status',
      sortable: true,
      render: (value, row) => {
        const status = row.projectStatus
        if (!status) return <span className="text-gray-500">N/A</span>
        
        // Handle both string status (Active/Inactive) and object status
        const statusName = typeof status === 'string' ? status : status.projectStatusName || 'N/A'
        const statusColor = typeof status === 'string' 
          ? (status === 'Active' ? '#10B981' : '#6B7280')
          : (status.statusColor || '#6B7280')
        
        return (
          <span 
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
            style={{ 
              backgroundColor: `${statusColor}20`,
              color: statusColor,
              border: `1px solid ${statusColor}40`
            }}
          >
            {statusName}
          </span>
        )
      }
    },
    {
      key: 'projectManager',
      label: 'Manager',
      sortable: true,
      render: (value, row) => {
        const manager = row.projectManager
        if (!manager) return <span className="text-gray-500">N/A</span>
        
        // Handle both object and string cases
        const managerName = typeof manager === 'string' 
          ? manager 
          : (manager.employeeName || manager.name || `${manager.firstName || ''} ${manager.lastName || ''}`.trim() || 'N/A')
        
        return <div className="font-medium text-gray-900">{managerName}</div>
      }
    },
    {
      key: 'plannedStartDate',
      label: 'Start Date',
      sortable: true,
      render: (value, row) => {
        if (!value) return <span className="text-gray-500">N/A</span>
        
        const formatDate = (dateString) => {
          const date = new Date(dateString)
          const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
          const day = String(date.getDate()).padStart(2, '0')
          const month = months[date.getMonth()]
          const year = date.getFullYear()
          return `${day}-${month}-${year}`
        }
        
        return <div className="font-medium text-gray-900">{formatDate(value)}</div>
      }
    },
    {
      key: 'sowAmount',
      label: 'Budget',
      sortable: true,
      render: (value, row) => {
        if (!value) return <span className="text-gray-500">N/A</span>
        return <div className="font-medium text-gray-900">{`${row.currency || 'USD'} ${value.toLocaleString()}`}</div>
      }
    }
  ]

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Projects',
      path: '/projects',
      onClick: () => navigate('/projects')
    },
    {
      label: 'Transactions',
      path: '/projects?tab=transactions',
      onClick: () => navigate('/projects?tab=transactions')
    },
    {
      label: 'Project List',
      path: '/projects/list',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Project List</h1>
          <p>Create, view, and manage all projects</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn-primary"
            onClick={handleAddNew}
          >
            <Plus size={16} />
            Add New Project
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <FolderOpen size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+12%</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalProjects || 0}</div>
          <div className="metric-title">Total Projects</div>
          <div className="metric-subtitle">vs last month</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <TrendingUp size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+8%</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeProjects || 0}</div>
          <div className="metric-title">Active Projects</div>
          <div className="metric-subtitle">Currently tracking</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <AlertTriangle size={16} />
            </div>
            <div className="metric-trend negative">
              <TrendingUp size={12} />
              <span>+3%</span>
            </div>
          </div>
          <div className="metric-value">{stats.overdueProjects || 0}</div>
          <div className="metric-title">Overdue Projects</div>
          <div className="metric-subtitle">Need attention</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+15%</span>
            </div>
          </div>
          <div className="metric-value">{stats.budgetUtilization || 0}%</div>
          <div className="metric-title">Budget Utilization</div>
          <div className="metric-subtitle">Overall efficiency</div>
        </div>
      </div>

      {/* Project List Table */}
      <div className="dashboard-content">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          onRevisionHistory={handleRevisionHistory}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          searchPlaceholder="Search projects..."
          emptyMessage="No projects found. Create your first project to get started!"
          noCard={true}
        />
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Project"
        message={`Are you sure you want to delete "${deleteItem?.projectName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Revision History Modal */}
      <RevisionHistoryModal
        isOpen={showRevisionHistory}
        onClose={() => setShowRevisionHistory(false)}
        data={selectedProjectData}
        title="Project Revision History"
      />
    </div>
  )
}

export default ProjectList
